import { APIResponse } from 'types/Request';
import { getCookie } from 'helpers';
import { getQueryString } from 'services/ApiService';
import config from 'config';

export interface Insurer {
  id: number;
  codigo: string;
  nombre: string;
}

export async function getInsurers(onlyActive = true): Promise<Insurer[]> {
  try {
    const url = new URL(`${ config.baseUrl }/insurers/${ onlyActive ? '?onlyActive=1' : '' }`);

    type Response = APIResponse<Insurer[]>

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: getCookie('accessToken'),
      },
    });

    if (response.status !== 200) {
      console.error(response.statusText);
      throw new Error('Ocurrió un error al recuperar los aranceles.');
    }

    const content = await response.json() as Response;

    if (content.error || ! content.payload) {
      console.error(content.error);
      throw new Error('Ocurrió un error al recuperar los aranceles.');
    }

    return content.payload;
  } catch (err) {
    throw new Error('Ocurrió un problema inesperado al momento de recuperar el certificado de praxis médica.');
  }
}

interface OsaCobrarRow {
  matricula: number;
  fecha: string;
  periodo: string;
  obras: string;
  afiliado: string;
  modulo: string;
  diagno: string;
  nro_auto: string;
  sesiones: number;
  facturado: number;
  coseguro: number;
  nro_os: number;
  digito_os: number,
}

interface IOMAaCobrarRow {
  matricula: number;
  fecha: string;
  periodo: string;
  obras: string;
  afiliado: string;
  modulo: string;
  diagno: string;
  nro_auto: string;
  sesiones: number;
  facturado: number;
  coseguro: number;
}

interface DebitosRow {
  delegacion: string;
  matricula: number;
  importe: string;
  obraSocial: string;
  remito: number;
  afiliado: string;
  motivo: string;
  documentacion: string;
  mesPrestacion: string;
  coseguro: string;
}

export interface OSaCobrar {
  data: OsaCobrarRow[];
  insurers?: Insurer[];
  terms: string[];
  pagination: {
    current: number;
    total: number;
  };
  error?: string;
}

export interface IOMAaCobrar {
  data: IOMAaCobrarRow[];
  terms: string[];
  selectedTerm: string;
  pagination: {
    current: number;
    total: number;
  };
  error?: string;
}

export interface Debitos {
  data: DebitosRow[];
  insurer?: string;
  terms: string[];
  selectedTerm: string;
  pagination: {
    current: number;
    total: number;
  };
  error?: string;
}

interface OSaCobrarOptions {
  insurer_id?: number;
  term?: string;
  page?: number;
  order?: {
    field: keyof OsaCobrarRow;
    dir?: 'asc' | 'desc';
  }
}

export interface IOMAaCobrarOptions {
  term?: string;
  page?: number;
  order?: {
    field: keyof OsaCobrarRow;
    dir?: 'asc' | 'desc';
  }
}

export interface DebitosOptions {
  insurer?: string;
  term?: string;
  page?: number;
  order?: {
    field: keyof DebitosRow;
    dir?: 'asc' | 'desc';
  }
}

export async function getInsurersACobrar(options?: OSaCobrarOptions): Promise<OSaCobrar> {
  try {
    type Response = APIResponse<OSaCobrar>

    const queryString = getQueryString(options);
    const url = new URL(`${ config.baseUrl }/insurers/acobrar${queryString}`);

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: getCookie('accessToken'),
      },
    });

    if (response.status !== 200) {
      console.error(response.statusText);
      throw new Error('Ocurrió un error al recuperar los cobros.');
    }

    const content = await response.json() as Response;

    if (content.error || ! content.payload) {
      console.error(content.error);
      throw new Error('Ocurrió un error al recuperar los cobros.');
    }

    return content.payload;
  } catch (err) {
    throw new Error('Ocurrió un problema inesperado al momento de recuperar los cobros');
  }
}

export async function getIOMAACobrar(options?: IOMAaCobrarOptions): Promise<IOMAaCobrar> {
  try {
    type Response = APIResponse<IOMAaCobrar>

    const queryString = getQueryString(options);
    const url = new URL(`${ config.baseUrl }/insurers/iomaacobrar${queryString}`);

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: getCookie('accessToken'),
      },
    });

    if (response.status !== 200) {
      console.error(response.statusText);
      throw new Error('Ocurrió un error al recuperar los cobros.');
    }

    const content = await response.json() as Response;

    if (content.error || ! content.payload) {
      console.error(content.error);
      throw new Error('Ocurrió un error al recuperar los cobros.');
    }

    return content.payload;
  } catch (err) {
    throw new Error('Ocurrió un problema inesperado al momento de recuperar los cobros');
  }
}

export async function getDebitos(options?: DebitosOptions): Promise<Debitos> {
  try {
    type Response = APIResponse<Debitos>

    const queryString = getQueryString(options);
    const url = new URL(`${ config.baseUrl }/insurers/debitos${queryString}`);

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: getCookie('accessToken'),
      },
    });

    if (response.status !== 200) {
      console.error(response.statusText);
      throw new Error('Ocurrió un error al recuperar los debitos.');
    }

    const content = await response.json() as Response;

    if (content.error || ! content.payload) {
      console.error(content.error);
      throw new Error('Ocurrió un error al recuperar los debitos.');
    }

    return content.payload;
  } catch (err) {
    throw new Error('Ocurrió un problema inesperado al momento de recuperar los debitos');
  }
}
